import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { overlay } from 'overlay-kit';
import { Col, Row } from '@/lib/utils';
import { DemiLightText, MediumText, RegularText } from '@/lib/Text';
import SvgIcon from '@/components/SvgIcon';
import DialogShell from '../../Settlement/components/exceptionRates/DialogShell';
import {
    isTemplateCategoryBulkSessionAlive,
    isTemplateCategoryBulkSessionStorageEvent,
    readTemplateCategoryBulkSessionKey,
} from './bulkFlowSession';
import {
    TemplateCategoryBulkJob,
    TemplateCategoryBulkPendingJob,
    TemplateCategoryBulkPreview,
    canRevertTemplateCategoryBulkJob,
    classifyTemplateCategoryBulkFailure,
    createTemplateCategoryBulkRequestId,
    decideTemplateCategoryBulkStart,
    describeTemplateCategoryBulkFee,
    describeTemplateCategoryBulkResult,
    describeTemplateCategoryBulkSweepIncomplete,
    getSettlementClassificationErrorCode,
    getSettlementClassificationErrorMessage,
    getTemplateCategoryBulkPreview,
    describeTemplateCategoryBulkResumeFee,
    loadPendingRevert,
    savePendingRevert,
    clearPendingRevert,
    isTemplateCategoryBulkTooLarge,
    isTemplateCategoryBulkUnavailable,
    objectParticleOf,
    templateCategoryBulkCategoryLabel,
    templateCategoryBulkProtectedCount,
    useApplyTemplateCategoryBulk,
    useRevertTemplateCategoryBulk,
    useSettlementCategories,
} from '@/features/settlementClassification';

/**
 * 상품의 정산 분류를 바꿔 저장한 **뒤에** 이미 발급된 수강권에도 적용할지 묻는 흐름.
 *
 * 상품 모달은 기존대로 저장 즉시 닫힌다. 이 흐름은 overlay 로 떠서 모달이 닫혀도 살아 있다
 * (결과 알림의 「되돌리기」가 모달 수명에 묶이면 안 된다).
 *
 * - 미리보기 404(구서버·없는 상품)·403(권한) → 아무것도 띄우지 않는다. 상품만 바뀐 기존 동작.
 * - pendingJob 이 대상 0건보다 먼저 — 끝나지 않은 작업 이어 하기.
 * - 대상 0건 → 아무것도 띄우지 않는다.
 * - 숫자·수업료가 바뀌기 시작하는 달은 서버 값 그대로. 웹은 세지 않는다.
 */

const TOAST_MS = 12000;
const fmt = (n: number) => n.toLocaleString('ko-KR');

type Phase =
    | { name: 'loading' }
    /** afterJob = 이 흐름에서 방금 쓰기가 끝났다(재개 성공 등). 그때는 「아직 그대로예요」라고 하지 않는다. */
    | {
          name: 'previewError';
          message: string;
          afterJob?: TemplateCategoryBulkJob | null;
      }
    | { name: 'confirm' }
    /**
     * confirm = 재개 화면에 보여 줄 현재 수업료 영향(GET 미리보기 또는 409 의 새 미리보기). 재개는 항상 이 안내를 보여 준 뒤에만 보낸다.
     * feeChanged = 409 로 안내가 바뀌었다 — 강조하고 사용자가 다시 눌러야 진행한다.
     * 보내는 hash 는 언제나 pending.previewHash(서버가 넣어 둔 이어 실행 토큰)다 — 미리보기의 previewHash 는 새 적용용이라 쓰지 않는다.
     */
    | {
          name: 'resume';
          pending: TemplateCategoryBulkPendingJob;
          confirm?: TemplateCategoryBulkPreview | null;
          feeChanged?: boolean;
      }
    | { name: 'revertConfirm'; job: TemplateCategoryBulkJob }
    | { name: 'idle' };

type Toast = { text: string; job: TemplateCategoryBulkJob | null; id: number };

const SecondaryButton = ({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={`h-[44px] px-[18px] rounded-[10px] bg-[#F4F5F7] border-0 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer hover:bg-[#ECEEF1]'}`}
    >
        <RegularText fontSize={15} color="#3D4244">
            {label}
        </RegularText>
    </button>
);

const PrimaryButton = ({ label, onClick, disabled }: { label: string; onClick: () => void; disabled?: boolean }) => (
    <button
        type="button"
        disabled={disabled}
        onClick={onClick}
        className={`h-[44px] px-[22px] rounded-[10px] bg-primary border-0 ${disabled ? 'bg-opacity-30 cursor-not-allowed' : 'cursor-pointer hover:brightness-95'}`}
    >
        <MediumText fontSize={15} color="white">
            {label}
        </MediumText>
    </button>
);

const Notice = ({ text, tone = 'error' }: { text: string; tone?: 'error' | 'info' }) => (
    <RegularText
        fontSize={14}
        lineHeight={21}
        color={tone === 'error' ? '#D93043' : '#1B5AD4'}
        className="mt-[14px] block"
    >
        {text}
    </RegularText>
);

const Stat = ({ label, value }: { label: string; value: number }) => (
    <Col className="flex-1 rounded-[12px] bg-[#F6F8FA] px-[14px] py-[12px] gap-[2px]">
        <DemiLightText fontSize={13} color="#6B7280">
            {label}
        </DemiLightText>
        <MediumText fontSize={22} lineHeight={30} color="#1A1A1A">
            {`${fmt(value)}장`}
        </MediumText>
    </Col>
);

const NoteLine = ({ text, tone = 'calm' }: { text: string; tone?: 'calm' | 'warn' }) => (
    <Row className="items-start gap-[8px]">
        <span
            className="mt-[8px] h-[4px] w-[4px] shrink-0 rounded-full"
            style={{ background: tone === 'warn' ? '#C2410C' : '#9CA3AF' }}
        />
        <RegularText fontSize={14} lineHeight={21} color={tone === 'warn' ? '#C2410C' : '#4B5563'}>
            {text}
        </RegularText>
    </Row>
);

/** 결과 알림. 마우스를 올려 두면 사라지지 않는다(되돌리기를 누르려는 중에 닫히면 안 된다). */
const ResultToast = ({
    toast,
    onRevert,
    onClose,
}: {
    toast: Toast;
    onRevert: (() => void) | null;
    onClose: () => void;
}) => {
    const [hovered, setHovered] = useState(false);
    // 적용 중 새로 생긴 수강권이 남았으면(서버 sweepIncomplete) 할 일이 있으니 저절로 닫지 않는다.
    const sweepNotice = describeTemplateCategoryBulkSweepIncomplete(toast.job);
    useEffect(() => {
        if (hovered || sweepNotice) return undefined;
        const timer = window.setTimeout(onClose, TOAST_MS);
        return () => window.clearTimeout(timer);
    }, [hovered, sweepNotice, onClose, toast.id]);

    return createPortal(
        <div
            role="status"
            aria-live="polite"
            className="fixed bottom-[40px] left-1/2 -translate-x-1/2 z-[99999] flex items-center gap-[16px] rounded-[12px] bg-[#202632] pl-[20px] pr-[12px] py-[12px] shadow-lg"
            style={{ maxWidth: 'calc(100vw - 32px)' }}
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
        >
            <Col className="min-w-0">
                <RegularText fontSize={15} lineHeight={22} color="#FFFFFF">
                    {toast.text}
                </RegularText>
                {sweepNotice && (
                    <RegularText fontSize={14} lineHeight={21} color="#FCD34D">
                        {sweepNotice}
                    </RegularText>
                )}
                {onRevert && (
                    <RegularText fontSize={13} lineHeight={19} color="#AEB6C2">
                        상품 창에서도 되돌릴 수 있어요.
                    </RegularText>
                )}
            </Col>
            {onRevert && (
                <button
                    type="button"
                    onClick={onRevert}
                    className="shrink-0 border-0 bg-transparent px-[6px] py-[4px] cursor-pointer rounded-[6px] hover:bg-white/10"
                >
                    <MediumText fontSize={15} color="#8CB4FF">
                        되돌리기
                    </MediumText>
                </button>
            )}
            <button
                type="button"
                aria-label="알림 닫기"
                onClick={onClose}
                className="shrink-0 border-0 bg-transparent p-[6px] cursor-pointer rounded-[6px] hover:bg-white/10"
            >
                <SvgIcon name="SvgX" size={12} stroke="#AEB6C2" strokeWidth={2} />
            </button>
        </div>,
        document.body,
    );
};

type ExternalRequest = { type: 'preview' } | { type: 'revert'; job: TemplateCategoryBulkJob };

/** 떠 있는 흐름(세션·센터·상품) → 그 흐름에 요청을 넘기는 손잡이. 결과 알림이 떠 있는 동안
 *  상품 모달의 「되돌리기」·「이어서 끝내기」나 다시 저장한 일이 그 흐름으로 이어지게 한다. */
const openFlows = new Map<string, (request: ExternalRequest) => void>();

/**
 * 같은 탭 안의 로그아웃·재로그인 확인 주기. 다른 탭의 변화는 'storage' 이벤트로 즉시 잡고,
 * 요청 직전·응답 직후에도 다시 확인하므로 이 주기는 화면을 닫는 시점만 정한다. 검사 한 번에 저장소 키 순회와
 * AES 복호화 2회(토큰·인증 객체)·JSON 파싱이 든다 — 흐름당 초당 1회, HTTP 요청은 없다.
 */
const SESSION_CHECK_MS = 1000;

/**
 * 이어 하기에 쓸 pendingJob. 409 재확인(§4-1)이면 details.resume 의 키·토큰·영향이 이긴다.
 * 그 밖에는 미리보기의 pendingJob 그대로 — PENDING_EXISTS details 의 previewHash 는 토큰이 아닐 수 있어 쓰지 않는다.
 */
const resumePendingFrom = (
    pending: TemplateCategoryBulkPendingJob,
    resume?: { requestId: string; previewHash: string; feeImpact: any } | null,
    preview?: TemplateCategoryBulkPreview | null,
): TemplateCategoryBulkPendingJob => {
    if (resume)
        return {
            ...pending,
            requestId: resume.requestId,
            previewHash: resume.previewHash,
            feeImpact: resume.feeImpact,
        };
    if (preview && pending.kind === 'APPLY' && !pending.feeImpact) {
        return {
            ...pending,
            previewHash: preview.previewHash,
            feeImpact: {
                exceptionRatesEffective: preview.exceptionRatesEffective,
                feeChangeStartMonth: preview.feeChangeStartMonth,
            },
        };
    }
    return pending;
};

export const TemplateCategoryBulkFlow = ({
    centerId,
    templateId,
    revertJob,
    sessionKey,
    flowKey,
    onDone,
}: {
    centerId: string;
    templateId: string;
    /** 상품 모달의 「되돌리기」에서 열었을 때 — 미리보기 없이 되돌리기 확인부터. */
    revertJob?: TemplateCategoryBulkJob;
    /** 연 세션. 바뀌거나 사라지면 즉시 닫고 늦은 응답은 버린다. 없으면(테스트) 확인하지 않는다. */
    sessionKey?: string | null;
    /** 떠 있는 흐름 표에 올릴 이름(세션·센터·상품). 같은 흐름으로 들어오는 요청을 받는다. */
    flowKey?: string;
    onDone: () => void;
}) => {
    const { data: categoriesState } = useSettlementCategories({ centerId });
    const categories = useMemo(() => categoriesState?.categories ?? [], [categoriesState]);
    const applyMutation = useApplyTemplateCategoryBulk({ centerId, templateId });
    const revertMutation = useRevertTemplateCategoryBulk({
        centerId,
        templateId,
    });

    const [phase, setPhase] = useState<Phase>(
        revertJob ? { name: 'revertConfirm', job: revertJob } : { name: 'loading' },
    );
    /** 되돌리기 확인을 결과 알림에서 열었나 — 취소하면 알림으로 돌아간다. 모달에서 열었으면 그냥 닫는다. */
    const revertFromToastRef = useRef(false);
    const [preview, setPreview] = useState<TemplateCategoryBulkPreview | null>(null);
    const [notice, setNotice] = useState<{
        text: string;
        tone: 'error' | 'info';
    } | null>(null);
    /** 이 상태에서는 확인 버튼이 「다시 확인」(미리보기부터) 이 된다. */
    const [needsRepreview, setNeedsRepreview] = useState(false);
    const [stopped, setStopped] = useState(false);
    const [toast, setToast] = useState<Toast | null>(null);

    /** 확인 버튼을 처음 누를 때 만들고, 응답을 못 받은 재시도에는 그대로 쓴다. */
    const requestIdRef = useRef<string | null>(null);
    /** 연타 방지 — isLoading 은 다음 렌더에야 반영된다. */
    const submittingRef = useRef(false);
    /** 되돌리기 확인 창 하나에 키 하나. 응답을 못 받은 재시도·결과 확인에는 같은 키(서버가 재생한다). */
    const revertRequestIdRef = useRef<string | null>(null);
    /** 이어 하기에 **성공한** job — 그 뒤에도 PENDING 으로 보이면 고리를 돌지 않고 멈춘다.
     *  실패(잠금 충돌 등)는 적지 않는다. 적으면 잠금이 풀린 뒤에도 재개 버튼이 돌아오지 않는다. */
    const resumedJobIdsRef = useRef<Set<string>>(new Set());
    const toastSeqRef = useRef(0);
    /** 적용·되돌리기가 결과 미확인(200 인데 본문 없음) 상태 — 확인 버튼이 「결과 확인」이 된다. */
    const [unconfirmed, setUnconfirmed] = useState(false);

    /* ---------------- 세션 묶기: 바뀌면 즉시 닫고, 늦은 응답은 버린다 ---------------- */
    const [gone, setGone] = useState(false);
    const goneRef = useRef(false);
    const onDoneRef = useRef(onDone);
    onDoneRef.current = onDone;
    const isSessionAlive = useCallback(() => {
        if (goneRef.current) return false;
        if (sessionKey === undefined) return true;
        return !!sessionKey && isTemplateCategoryBulkSessionAlive(sessionKey);
    }, [sessionKey]);
    /** 이 흐름이 보낸 요청 전체의 중단 신호. 창 종료·세션 변경 때 진행 중 요청을 끊는다. */
    // effect setup 마다 새로 만든다 — StrictMode 의 setup→cleanup→setup 에서 끊긴 컨트롤러를 계속 쓰지 않게.
    const abortRef = useRef<AbortController | null>(null);
    /** 요청을 보내는 그 순간의 신호. */
    const currentSignal = () => abortRef.current?.signal;
    const leave = useCallback(() => {
        if (goneRef.current) return;
        goneRef.current = true;
        abortRef.current?.abort();
        setGone(true);
        onDoneRef.current();
    }, []);
    useEffect(() => {
        const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
        abortRef.current = controller;
        return () => controller?.abort();
    }, []);
    /** await 뒤에 부른다. 세션이 바뀌었으면 닫고 false — 호출부는 아무것도 그리지 않고 돌아간다. */
    const stillHere = useCallback(() => {
        if (isSessionAlive()) return true;
        leave();
        return false;
    }, [isSessionAlive, leave]);
    useEffect(() => {
        if (sessionKey === undefined) return undefined;
        if (!isSessionAlive()) {
            leave();
            return undefined;
        }
        // 다른 탭의 로그아웃·재로그인 — react-secure-storage 메모리는 모르므로 원문 변경 이벤트로 잡는다.
        const onStorage = (event: StorageEvent) => {
            if (isTemplateCategoryBulkSessionStorageEvent(event) && !isSessionAlive()) leave();
        };
        window.addEventListener('storage', onStorage);
        // 같은 탭의 변화는 storage 이벤트가 오지 않는다.
        const timer = window.setInterval(() => {
            if (!isSessionAlive()) leave();
        }, SESSION_CHECK_MS);
        return () => {
            window.removeEventListener('storage', onStorage);
            window.clearInterval(timer);
        };
    }, [sessionKey, isSessionAlive, leave]);

    const busy = applyMutation.isLoading || revertMutation.isLoading;

    const showToast = useCallback(
        (job: TemplateCategoryBulkJob | null, text?: string) => {
            toastSeqRef.current += 1;
            // 분류 목록을 아직 못 읽었으면 이름 없이 「42장에 적용했어요」.
            const categoryName = !job
                ? undefined
                : !job.toKey
                  ? '미분류'
                  : categories.find((c) => c.key === job.toKey)?.name;
            setToast({
                text: text ?? describeTemplateCategoryBulkResult(job, categoryName),
                job,
                id: toastSeqRef.current,
            });
        },
        [categories],
    );

    const resetConfirmState = () => {
        setNotice(null);
        setNeedsRepreview(false);
        setStopped(false);
        setUnconfirmed(false);
        requestIdRef.current = null;
    };

    const openFromPreview = useCallback((next: TemplateCategoryBulkPreview | null) => {
        const decision = decideTemplateCategoryBulkStart(next);
        if (decision === 'skip' || !next) {
            setPhase({ name: 'idle' });
            return;
        }
        if (decision === 'resume' && next.pendingJob) {
            if (resumedJobIdsRef.current.has(next.pendingJob.jobId)) {
                setPhase({
                    name: 'previewError',
                    message: '이어서 끝내지 못한 작업이 남아 있어요. 계속되면 라포채널로 문의해 주세요.',
                });
                return;
            }
            setPhase({ name: 'resume', pending: next.pendingJob, confirm: next });
            return;
        }
        setPreview(next);
        setPhase({ name: 'confirm' });
    }, []);

    const loadPreview = useCallback(
        async (afterJob?: TemplateCategoryBulkJob | null) => {
            setPhase({ name: 'loading' });
            resetConfirmState();
            const signal = currentSignal();
            try {
                const next = await getTemplateCategoryBulkPreview({
                    centerId,
                    templateId,
                    signal,
                });
                if (!stillHere()) return;
                openFromPreview(next);
            } catch (error) {
                // 내가 끊은 요청(창 종료·StrictMode 재마운트)의 실패는 화면에 올리지 않는다.
                if (signal?.aborted) return;
                if (!stillHere()) return;
                if (classifyTemplateCategoryBulkFailure(error as any, 'apply').type === 'authLost') {
                    setPhase({
                        name: 'previewError',
                        message: '로그인이 바뀌었거나 만료됐어요. 다시 로그인한 뒤 상품 창에서 확인해 주세요.',
                        afterJob: afterJob ?? null,
                    });
                    return;
                }
                if (isTemplateCategoryBulkUnavailable(error as any)) {
                    setPhase({ name: 'idle' });
                    return;
                }
                const message = getSettlementClassificationErrorCode(error as any)
                    ? getSettlementClassificationErrorMessage(error as any)
                    : '발급된 수강권을 확인하지 못했어요. 잠시 후 다시 시도해 주세요.';
                setPhase({ name: 'previewError', message, afterJob: afterJob ?? null });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [centerId, templateId, openFromPreview, stillHere],
    );

    useEffect(() => {
        if (!revertJob) loadPreview();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadPreview]);

    // 띄울 것이 하나도 남지 않으면 overlay 를 내린다.
    useEffect(() => {
        if (phase.name === 'idle' && !toast) onDone();
    }, [phase.name, toast, onDone]);

    const apply = async () => {
        if (submittingRef.current || !preview) return;
        if (!stillHere()) return; // 요청 직전 — 다른 탭에서 로그아웃했으면 보내지 않는다
        submittingRef.current = true;
        setNotice(null);
        if (!requestIdRef.current) requestIdRef.current = createTemplateCategoryBulkRequestId();
        try {
            const job = await applyMutation.mutateAsync({
                requestId: requestIdRef.current,
                previewHash: preview.previewHash,
                signal: currentSignal(),
            });
            if (!stillHere()) return;
            requestIdRef.current = null;
            setUnconfirmed(false);
            showToast(job);
            setPhase({ name: 'idle' });
        } catch (error) {
            if (!stillHere()) return;
            const action = classifyTemplateCategoryBulkFailure(error as any, 'apply');
            if (action.type !== 'unconfirmed') setUnconfirmed(false);
            switch (action.type) {
                case 'authLost':
                    // 재전송하지 않았다. 결과를 모르는 셈이라 키는 버리지 않고 더 보내지 않는다.
                    setStopped(true);
                    setNotice({ text: action.message, tone: 'error' });
                    break;
                case 'unconfirmed':
                    // 키를 버리지 않는다 — 「결과 확인」이 같은 키로 보내 서버가 결과를 재생한다.
                    setUnconfirmed(true);
                    setNotice({ text: action.message, tone: 'error' });
                    break;
                case 'stale':
                    // 쓰기 전에 거절됐다. 새 hash 에는 새 키를 쓴다.
                    requestIdRef.current = null;
                    if (decideTemplateCategoryBulkStart(action.preview) === 'confirm') {
                        // 서버는 수업료 영향(달)도 hash 에 넣는다 — 그게 바뀌었으면 그렇게 알린다.
                        const feeChanged =
                            describeTemplateCategoryBulkFee(preview).text !==
                            describeTemplateCategoryBulkFee(action.preview).text;
                        setPreview(action.preview);
                        setNotice({
                            text: feeChanged
                                ? '그 사이 수업료 영향이 바뀌어 다시 불러왔어요. 확인 후 눌러 주세요.'
                                : action.message,
                            tone: 'info',
                        });
                    } else if (action.preview.pendingJob) {
                        // 끝나지 않은 작업으로 넘어가도 서버가 준 새 수업료 안내를 버리지 않는다 — 재개 화면에 그대로 싣고 강조한다.
                        const nextPending = resumePendingFrom(action.preview.pendingJob, action.resume);
                        const feeChanged =
                            describeTemplateCategoryBulkFee(preview).text !==
                            describeTemplateCategoryBulkResumeFee(nextPending, action.preview)?.text;
                        setPhase({
                            name: 'resume',
                            pending: nextPending,
                            confirm: action.preview,
                            feeChanged,
                        });
                        setNotice({
                            text: feeChanged ? '수업료 안내가 바뀌었어요. 확인 후 다시 눌러 주세요.' : action.message,
                            tone: feeChanged ? 'error' : 'info',
                        });
                    } else {
                        showToast(null, '그 사이 바뀌어 적용할 수강권이 없어졌어요');
                        setPhase({ name: 'idle' });
                    }
                    break;
                case 'pending':
                    // 수업료 안내 없이 재개 화면을 띄우지 않는다 — 미리보기(pendingJob + 현재 영향)부터 다시 읽는다.
                    requestIdRef.current = null;
                    await loadPreview();
                    break;
                case 'retrySame':
                    setNotice({ text: action.message, tone: 'error' });
                    break;
                case 'repreview':
                    if (getSettlementClassificationErrorCode(error as any) === 'SETTLEMENT_BULK_IN_PROGRESS') {
                        // 잠금 충돌 — 응답을 못 받은 내 앞선 요청일 수도 있다. 키를 유지해 다시 누르면 그 결과가 재생된다.
                        setNotice({ text: action.message, tone: 'error' });
                        break;
                    }
                    requestIdRef.current = null;
                    setNeedsRepreview(true);
                    setNotice({ text: action.message, tone: 'error' });
                    break;
                case 'renewRequestId':
                    requestIdRef.current = null;
                    setNotice({ text: action.message, tone: 'error' });
                    break;
                case 'stop':
                    requestIdRef.current = null;
                    setStopped(true);
                    setNotice({ text: action.message, tone: 'error' });
                    break;
            }
        } finally {
            submittingRef.current = false;
        }
    };

    const resume = async (pending: TemplateCategoryBulkPendingJob) => {
        if (submittingRef.current) return;
        if (pending.kind === 'APPLY' ? !pending.previewHash : !pending.revertOfJobId) {
            setStopped(true);
            setNotice({
                text: '이어서 할 수 없는 작업이에요. 라포채널로 문의해 주세요.',
                tone: 'error',
            });
            return;
        }
        if (!stillHere()) return;
        submittingRef.current = true;
        setNotice(null);
        try {
            // 이어 하기는 그 job 의 키 그대로 — 결과 미확인 뒤 다시 눌러도 같은 키라 서버가 재생한다.
            const job =
                pending.kind === 'APPLY'
                    ? await applyMutation.mutateAsync({
                          requestId: pending.requestId,
                          // 서버가 pendingJob 에 넣어 둔 이어 실행 토큰. 화면에 보인 수업료 안내와 짝이다.
                          previewHash: pending.previewHash as string,
                          signal: currentSignal(),
                      })
                    : await revertMutation.mutateAsync({
                          jobId: pending.revertOfJobId as string,
                          requestId: pending.requestId,
                          signal: currentSignal(),
                      });
            if (pending.kind === 'REVERT' && pending.revertOfJobId) {
                // 끝난 되돌리기의 키는 지운다 — 남겨 두면 다음 「다시 정리」가 이 키로 나가 옛 결과만 재생한다.
                clearPendingRevert({ centerId, templateId, applyJobId: pending.revertOfJobId });
            }
            if (!stillHere()) return;
            resumedJobIdsRef.current.add(pending.jobId);
            showToast(job);
            // 이어 하기를 끝낸 뒤, 방금 바꾼 상품 분류로 맞출 대상이 남았는지 다시 본다.
            // 이 확인이 실패해도 방금 쓴 사실과 모순되는 「아직 그대로예요」를 띄우지 않게 결과를 넘긴다.
            await loadPreview(job);
        } catch (error) {
            if (!stillHere()) return;
            const action = classifyTemplateCategoryBulkFailure(
                error as any,
                pending.kind === 'APPLY' ? 'apply' : 'revert',
            );
            const code = getSettlementClassificationErrorCode(error as any);
            if (action.type === 'pending' && action.pending.jobId !== pending.jobId) {
                await loadPreview();
            } else if (code === 'SETTLEMENT_BULK_IN_PROGRESS') {
                // 잠금 충돌은 일시적이다. 재개 버튼을 그대로 두고 잠시 후 다시 누르게 한다.
                setNotice({ text: action.message, tone: 'error' });
            } else if (action.type === 'stale' && pending.kind === 'APPLY') {
                // 재개 직전 수업료 영향이 바뀌었다(서버 409 + 새 미리보기·새 이어 실행 토큰). 같은 requestId 를 유지한 채
                // 새 안내로 바꾸고 강조한다 — 사용자가 다시 눌러야 진행한다.
                const nextPending = resumePendingFrom(
                    action.preview.pendingJob ?? pending,
                    action.resume,
                    action.preview,
                );
                setPhase({
                    name: 'resume',
                    pending: nextPending,
                    confirm: action.preview,
                    feeChanged: true,
                });
                setNotice({
                    text: '수업료 안내가 바뀌었어요. 확인 후 다시 눌러 주세요.',
                    tone: 'error',
                });
            } else if (action.type === 'stale' || action.type === 'repreview') {
                await loadPreview();
            } else {
                if (action.type === 'stop' || action.type === 'authLost') setStopped(true);
                setNotice({ text: action.message, tone: 'error' });
            }
        } finally {
            submittingRef.current = false;
        }
    };

    const revert = async (job: TemplateCategoryBulkJob) => {
        if (submittingRef.current) return;
        if (!stillHere()) return;
        submittingRef.current = true;
        setNotice(null);
        // 확인 창 하나에 키 하나 — 응답 유실 재시도에 새 키를 쓰면 두 번째 REVERT 가 생겨 「0건」으로 끝난다.
        // 창을 취소했다 다시 열어도 결과를 모르는 되돌리기가 10분 안에 있으면 그 키를 다시 쓴다.
        const storeKey = { centerId, templateId, applyJobId: job.jobId };
        if (!revertRequestIdRef.current) {
            if (job.revertedByJobId) {
                // 「다시 정리」 = 목록이 이미 되돌림 완료로 보여 주는 상태에서의 새 의도. 보관 키(결과를 모를 때만 쓰는 것)는
                // 버리고 새 키로 보낸다 — 옛 키면 서버가 끝난 결과만 재생하고 남은 사본을 처리하지 않는다.
                clearPendingRevert(storeKey);
                revertRequestIdRef.current = createTemplateCategoryBulkRequestId();
            } else {
                revertRequestIdRef.current = loadPendingRevert(storeKey) ?? createTemplateCategoryBulkRequestId();
            }
        }
        const requestId = revertRequestIdRef.current;
        try {
            const result = await revertMutation.mutateAsync({
                jobId: job.jobId,
                requestId,
                signal: currentSignal(),
            });
            clearPendingRevert(storeKey);
            if (!stillHere()) return;
            revertRequestIdRef.current = null;
            setUnconfirmed(false);
            showToast(result);
            setPhase({ name: 'idle' });
        } catch (error) {
            if (!stillHere()) return;
            const action = classifyTemplateCategoryBulkFailure(error as any, 'revert');
            const uncertain =
                action.type === 'authLost' ||
                action.type === 'unconfirmed' ||
                action.type === 'retrySame' ||
                getSettlementClassificationErrorCode(error as any) === 'SETTLEMENT_BULK_IN_PROGRESS';
            if (uncertain) savePendingRevert({ ...storeKey, requestId });
            else clearPendingRevert(storeKey);
            if (action.type === 'authLost') {
                // 키는 위에서 결과 미확인으로 보관했다. 다시 로그인한 뒤 상품 창에서 같은 키로 확인된다.
                setStopped(true);
                setNotice({ text: action.message, tone: 'error' });
            } else if (action.type === 'unconfirmed') {
                setUnconfirmed(true);
                setNotice({ text: action.message, tone: 'error' });
            } else if (
                action.type === 'retrySame' ||
                getSettlementClassificationErrorCode(error as any) === 'SETTLEMENT_BULK_IN_PROGRESS'
            ) {
                // 같은 창에서는 성공·확정 실패 전까지 같은 키 — 잠금 충돌(내 앞선 요청이 아직 도는 중)도 마찬가지.
                setUnconfirmed(false);
                setNotice({ text: action.message, tone: 'error' });
            } else if (action.type === 'pending') {
                revertRequestIdRef.current = null;
                setUnconfirmed(false);
                await loadPreview();
            } else {
                revertRequestIdRef.current = null;
                setUnconfirmed(false);
                if (action.type === 'stop') setStopped(true);
                setNotice({ text: action.message, tone: 'error' });
            }
        } finally {
            submittingRef.current = false;
        }
    };

    const closeDialog = () => {
        if (busy) return;
        resetConfirmState();
        revertRequestIdRef.current = null;
        setPhase({ name: 'idle' });
    };

    const openRevertConfirm = (job: TemplateCategoryBulkJob, fromToast: boolean) => {
        setToast(null);
        setNotice(null);
        setStopped(false);
        setUnconfirmed(false);
        revertRequestIdRef.current = null;
        revertFromToastRef.current = fromToast;
        setPhase({ name: 'revertConfirm', job });
    };

    /* 결과 알림만 떠 있는 동안 상품 모달에서 들어온 요청(되돌리기·이어서 끝내기·다시 저장)을 이 흐름이 받는다.
       확인 창이 떠 있으면 그 창이 모달을 덮고 있으므로 무시한다. */
    const phaseRef = useRef(phase);
    phaseRef.current = phase;
    const externalRef = useRef<(request: ExternalRequest) => void>(() => undefined);
    externalRef.current = (request: ExternalRequest) => {
        if (submittingRef.current || goneRef.current) return;
        if (phaseRef.current.name !== 'idle') return;
        if (request.type === 'revert') openRevertConfirm(request.job, false);
        else loadPreview();
    };
    useEffect(() => {
        if (!flowKey) return undefined;
        const handle = (request: ExternalRequest) => externalRef.current(request);
        openFlows.set(flowKey, handle);
        return () => {
            if (openFlows.get(flowKey) === handle) openFlows.delete(flowKey);
        };
    }, [flowKey]);

    const closeToast = useCallback(() => setToast(null), []);

    const renderConfirm = (p: TemplateCategoryBulkPreview) => {
        const { total, active, expired, deleted } = p.counts.targets;
        const protectedCount = templateCategoryBulkProtectedCount(p);
        const fee = describeTemplateCategoryBulkFee(p);
        const tooLarge = isTemplateCategoryBulkTooLarge(p);
        const categoryName = templateCategoryBulkCategoryLabel(p.toKey, categories);
        const primaryDisabled = busy || stopped || tooLarge;
        return (
            <DialogShell title={p.templateName || '정산 분류 적용'} width={480} onClose={closeDialog} busy={busy}>
                <Col className="px-[24px] pb-[22px]">
                    <DemiLightText fontSize={14} lineHeight={21} color="#12A25D" className="mt-[8px] block">
                        상품은 이미 저장됐어요.
                    </DemiLightText>
                    <RegularText fontSize={17} lineHeight={27} color="#1A1A1A" className="mt-[6px] block">
                        {'이 상품으로 발급된 수강권 '}
                        <span className="font-noto-medium text-primary">{`${fmt(total)}장`}</span>
                        {'에도'}
                        <br />
                        <span className="font-noto-medium">{`「${categoryName}」`}</span>
                        {`${objectParticleOf(categoryName)} 적용할까요?`}
                    </RegularText>
                    <Row className="mt-[16px] gap-[8px]">
                        <Stat label="사용 중" value={active} />
                        <Stat label="만료" value={expired} />
                        {deleted > 0 && <Stat label="삭제" value={deleted} />}
                    </Row>
                    <Col className="mt-[16px] gap-[6px]">
                        <NoteLine text={fee.text} tone={fee.tone} />
                        {protectedCount > 0 && <NoteLine text={`직접 수정한 ${fmt(protectedCount)}장은 그대로 둬요`} />}
                        {/* 다른 센터 수강권 개수(counts.outOfScope)는 보이지 않는다 — 다른 센터 흔적을 화면에 내지 않는다(10/19). */}
                        <NoteLine text="발급 유형(신규/재등록)은 그대로예요" />
                    </Col>
                    {tooLarge && (
                        <Notice
                            text={`${fmt(p.maxTargets)}장이 넘어 한 번에 바꿀 수 없어요. 라포채널로 문의해 주세요.`}
                        />
                    )}
                    {notice && <Notice text={notice.text} tone={notice.tone} />}
                    <Row className="justify-end gap-[8px] mt-[22px]">
                        <SecondaryButton label="상품만 바꾸기" onClick={closeDialog} disabled={busy} />
                        {needsRepreview ? (
                            <PrimaryButton label="다시 확인" onClick={() => loadPreview()} disabled={busy} />
                        ) : (
                            <PrimaryButton
                                label={busy ? '적용하는 중…' : unconfirmed ? '결과 확인' : '발급된 수강권에도 적용'}
                                onClick={apply}
                                disabled={primaryDisabled}
                            />
                        )}
                    </Row>
                </Col>
            </DialogShell>
        );
    };

    const renderResume = (pending: TemplateCategoryBulkPendingJob, confirm?: TemplateCategoryBulkPreview | null) => {
        // 수업료 문구는 pendingJob.feeImpact(재개 토큰이 승인하는 값, §4-1). 구서버면 미리보기 현재 계산값.
        const resumeFee = describeTemplateCategoryBulkResumeFee(pending, confirm);
        return (
            <DialogShell title="끝나지 않은 작업이 있어요" width={440} onClose={closeDialog} busy={busy}>
                <Col className="px-[24px] pb-[22px]">
                    <RegularText
                        fontSize={15}
                        lineHeight={24}
                        color="#3D4244"
                        className="mt-[10px] block whitespace-pre-line"
                    >
                        {pending.kind === 'APPLY'
                            ? '이 상품의 정산 분류 일괄 적용이 중간에 멈춰 있어요.\n이어서 끝낼까요?'
                            : '이 상품의 일괄 적용 되돌리기가 중간에 멈춰 있어요.\n이어서 끝낼까요?'}
                    </RegularText>
                    {resumeFee && (
                        <Col className="mt-[14px] gap-[6px]">
                            <NoteLine text={resumeFee.text} tone={resumeFee.tone} />
                        </Col>
                    )}
                    {notice && <Notice text={notice.text} tone={notice.tone} />}
                    <Row className="justify-end gap-[8px] mt-[22px]">
                        <SecondaryButton label="나중에" onClick={closeDialog} disabled={busy} />
                        <PrimaryButton
                            label={busy ? '마무리하는 중…' : '이어서 끝내기'}
                            onClick={() => resume(pending)}
                            disabled={busy || stopped}
                        />
                    </Row>
                </Col>
            </DialogShell>
        );
    };

    const renderRevertConfirm = (job: TemplateCategoryBulkJob) => {
        const applied = job.counts?.applied ?? 0;
        const cancel = () => {
            if (busy) return;
            setNotice(null);
            setStopped(false);
            setUnconfirmed(false);
            revertRequestIdRef.current = null;
            setPhase({ name: 'idle' });
            if (revertFromToastRef.current) showToast(job);
        };
        // 이미 되돌린 적용을 다시 되돌리기 = 「다시 정리」(계약 §3·§5). 되돌리는 사이·직후 저장된 사본처럼
        // 남은 것만 돌린다 — 이미 되돌린 것은 서버가 빼므로 보통 0장이고 다시 눌러도 안전하다.
        const reclean = !!job.revertedByJobId;
        return (
            <DialogShell
                title={reclean ? '다시 정리할까요?' : '적용을 되돌릴까요?'}
                width={440}
                onClose={cancel}
                busy={busy}
            >
                <Col className="px-[24px] pb-[22px]">
                    {reclean ? (
                        <RegularText
                            fontSize={16}
                            lineHeight={25}
                            color="#1A1A1A"
                            className="mt-[10px] block whitespace-pre-line"
                        >
                            {'되돌린 뒤 남은 수강권을 원래 분류로 돌려요.\n남은 게 없으면 0장으로 끝나요.'}
                        </RegularText>
                    ) : (
                        <RegularText fontSize={16} lineHeight={25} color="#1A1A1A" className="mt-[10px] block">
                            {'적용 때 바꾼 '}
                            <span className="font-noto-medium">{`${fmt(applied)}장`}</span>
                            {'을 원래 분류로 돌려요.'}
                        </RegularText>
                    )}
                    <Col className="mt-[14px] gap-[6px]">
                        {/* 서버는 적용 뒤 생긴 인계·나눔·양도 사본도 함께 되돌린다(계약 §3). 되돌리기 미리보기가 없어
                            정확한 수는 결과 알림에서 알린다 — 확인 창 숫자가 과소 안내가 되지 않게 적는다. */}
                        <NoteLine text="그 뒤 생긴 인계·나눔 사본도 함께 돌려요" />
                        <NoteLine text="그 사이 직접 수정한 수강권은 그대로 둬요" />
                        <NoteLine text="상품의 정산 분류는 그대로예요. 필요하면 상품에서 바꿔 주세요" />
                    </Col>
                    {notice && <Notice text={notice.text} tone={notice.tone} />}
                    <Row className="justify-end gap-[8px] mt-[22px]">
                        <SecondaryButton label="취소" onClick={cancel} disabled={busy} />
                        <PrimaryButton
                            label={
                                busy ? '되돌리는 중…' : unconfirmed ? '결과 확인' : reclean ? '다시 정리' : '되돌리기'
                            }
                            onClick={() => revert(job)}
                            disabled={busy || stopped}
                        />
                    </Row>
                </Col>
            </DialogShell>
        );
    };

    const renderPreviewError = (message: string, afterJob?: TemplateCategoryBulkJob | null) => {
        // 방금 이 흐름에서 쓰기가 끝났으면(재개 성공 뒤 확인 실패) 성공 사실을 먼저 말하고, 「아직 그대로」라고 하지 않는다.
        const afterName = afterJob
            ? !afterJob.toKey
                ? '미분류'
                : categories.find((c) => c.key === afterJob.toKey)?.name
            : undefined;
        return (
            <DialogShell title="정산 분류 적용" width={420} onClose={closeDialog}>
                <Col className="px-[24px] pb-[22px]">
                    <RegularText fontSize={15} lineHeight={24} color="#3D4244" className="mt-[10px] block">
                        {afterJob ? `${describeTemplateCategoryBulkResult(afterJob, afterName)}.` : message}
                    </RegularText>
                    <DemiLightText fontSize={13} lineHeight={20} color="#6B7280" className="mt-[6px] block">
                        {afterJob
                            ? '남은 확인은 상품 창에서 다시 볼 수 있어요.'
                            : '상품은 저장됐어요. 발급된 수강권은 아직 그대로예요.'}
                    </DemiLightText>
                    <Row className="justify-end gap-[8px] mt-[22px]">
                        <SecondaryButton label="닫기" onClick={closeDialog} />
                        <PrimaryButton label="다시 확인" onClick={() => loadPreview(afterJob)} />
                    </Row>
                </Col>
            </DialogShell>
        );
    };

    const toastRevert =
        toast && phase.name !== 'revertConfirm' && canRevertTemplateCategoryBulkJob(toast.job)
            ? () => openRevertConfirm(toast.job as TemplateCategoryBulkJob, true)
            : null;

    if (gone) return null;
    return (
        <>
            {phase.name === 'confirm' && preview && renderConfirm(preview)}
            {phase.name === 'resume' && renderResume(phase.pending, phase.confirm)}
            {phase.name === 'revertConfirm' && renderRevertConfirm(phase.job)}
            {phase.name === 'previewError' && renderPreviewError(phase.message, phase.afterJob)}
            {toast && phase.name !== 'revertConfirm' && (
                <ResultToast key={toast.id} toast={toast} onRevert={toastRevert} onClose={closeToast} />
            )}
        </>
    );
};

/**
 * 상품 저장 성공 뒤(또는 상품 모달의 「이어서 끝내기」) 부른다. 띄울 것이 없으면 아무것도 보이지 않고 스스로 내려간다.
 * revertJob 을 주면 그 적용의 되돌리기 확인부터 연다(상품 모달의 「되돌리기」).
 */
export const openTemplateCategoryBulkFlow = ({
    centerId,
    templateId,
    revertJob,
    sessionKey,
}: {
    centerId: string;
    templateId: string;
    revertJob?: TemplateCategoryBulkJob;
    /** 연 세션. 저장을 누른 때의 값을 넘기면 그사이 계정이 바뀐 경우를 걸러 낸다. 없으면 지금 값. */
    sessionKey?: string | null;
}) => {
    if (!centerId || !templateId) return;
    const session = sessionKey === undefined ? readTemplateCategoryBulkSessionKey() : sessionKey;
    // 로그아웃했거나, 저장을 누른 뒤 다른 계정·센터로 바뀌었으면 열지 않는다.
    if (!session || session !== readTemplateCategoryBulkSessionKey()) return;
    if (!session.startsWith(`${centerId}|`)) return;
    const flowKey = `${session}|${templateId}`;
    // 같은 상품의 흐름이 이미 떠 있으면(결과 알림 등) 새로 띄우지 않고 그 흐름에 넘긴다 — 확인 창은 하나만.
    const existing = openFlows.get(flowKey);
    if (existing) {
        existing(revertJob ? { type: 'revert', job: revertJob } : { type: 'preview' });
        return;
    }
    // 마운트 전에 두 번째 호출이 와도 하나만 뜨게 자리를 먼저 잡는다(마운트하면 실제 손잡이로 바뀐다).
    openFlows.set(flowKey, () => undefined);
    overlay.open(({ unmount }) => (
        <TemplateCategoryBulkFlow
            centerId={centerId}
            templateId={templateId}
            revertJob={revertJob}
            sessionKey={session}
            flowKey={flowKey}
            onDone={() => {
                openFlows.delete(flowKey);
                unmount();
            }}
        />
    ));
};

export default TemplateCategoryBulkFlow;
